import * as XLSX from 'xlsx';
import {
  BulkValidateDepartmentInputSchema,
  type BulkValidateDepartmentInput
} from '@bliks-io/contracts';

export interface ParsedDepartment {
  data: BulkValidateDepartmentInput;
  isValid: boolean;
  errors?: string[];
  rowNumber: number;
}

export interface ExcelParseResult {
  departments: ParsedDepartment[];
  totalRows: number;
  validRows: number;
  invalidRows: number;
}

/**
 * Parse Excel file and validate department data
 * Expected columns: Name, Abbreviation, Description, Parent Abbreviation, Manager Email
 */
export async function parseDepartmentsFromExcel(file: File): Promise<ExcelParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });

        // Get first sheet (skip instructions sheet if present)
        const sheetName =
          workbook.SheetNames.find((name) => name !== 'Instructions') || workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData: unknown[] = XLSX.utils.sheet_to_json(worksheet);

        const departments: ParsedDepartment[] = [];
        let validRows = 0;
        let invalidRows = 0;

        jsonData.forEach((row, index) => {
          const rowNumber = index + 2; // +2 because: 0-indexed + 1 for header row

          try {
            // Map Excel columns to department fields
            const rowData = row as Record<string, unknown>;
            const departmentData: BulkValidateDepartmentInput = {
              name: String(rowData['Name'] || ''),
              abbreviation: String(rowData['Abbreviation'] || ''),
              description: rowData['Description'] ? String(rowData['Description']) : undefined,
              parentAbbreviation: rowData['Parent Abbreviation']
                ? String(rowData['Parent Abbreviation'])
                : undefined,
              managerEmail: rowData['Manager Email'] ? String(rowData['Manager Email']) : undefined
            };

            // Validate against schema (basic validation only)
            const validated = BulkValidateDepartmentInputSchema.parse(departmentData);

            departments.push({
              data: validated,
              isValid: true,
              rowNumber
            });
            validRows++;
          } catch (error) {
            invalidRows++;
            let errors: string[] = [];

            if (error && typeof error === 'object' && 'issues' in error) {
              // ZodError has an 'issues' property
              const zodError = error as {
                issues: Array<{ path: Array<string | number>; message: string }>;
              };
              errors = zodError.issues.map((err) => `${err.path.join('.')}: ${err.message}`);
            } else if (error instanceof Error) {
              errors = [error.message];
            } else {
              errors = ['Unknown validation error'];
            }

            const rowData = row as Record<string, unknown>;
            departments.push({
              data: {
                name: String(rowData['Name'] || ''),
                abbreviation: String(rowData['Abbreviation'] || ''),
                description: rowData['Description'] ? String(rowData['Description']) : undefined,
                parentAbbreviation: rowData['Parent Abbreviation']
                  ? String(rowData['Parent Abbreviation'])
                  : undefined,
                managerEmail: rowData['Manager Email']
                  ? String(rowData['Manager Email'])
                  : undefined
              },
              isValid: false,
              errors,
              rowNumber
            });
          }
        });

        resolve({
          departments,
          totalRows: jsonData.length,
          validRows,
          invalidRows
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });
}
